import { useEffect, useState, type FormEvent } from "react";
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Panel,
  PanelContent,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@diametral/design-system/react";

import {
  useAddStream,
  useArchiveStream,
  usePatchProject,
  usePatchStream,
  useSetLinks,
} from "../../api/projects";
import type { ProjectDetail, ProjectLink, ProjectPatchInput, Stream, Weather } from "../../api/types";
import { weatherLabel } from "../../components/Pilot";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { SelectField } from "../../components/SelectField";
import { TechnologiesField } from "../../components/TechnologiesField";
import { t, useTranslation } from "../../lib/i18n";
import { TRL_LEVELS, trlLabel } from "../../lib/reference";
import { notifySuccess } from "../../lib/toast";

/** The label is read in the current language each time (`trlLabel` is translated). */
export const TRL_OPTIONS = TRL_LEVELS.map((level) => ({
  value: String(level),
  get label() {
    return `TRL ${level} · ${trlLabel(level)}`;
  },
}));

/** Same rule as the backend (`projects.set_links`). */
export function linkUrlError(url: string): string | null {
  const value = url.trim();
  if (!value) return null;
  return /^(https:\/\/|git@|ssh:\/\/)/.test(value) ? null : t("Expected address: https://…, git@… or ssh://…");
}

function sameList(a: string[], b: string[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function InfoSection({ project, isDar }: { project: ProjectDetail; isDar: boolean }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(project.title);
  const [description, setDescription] = useState(project.description);
  const [trl, setTrl] = useState(String(project.trl));
  const [technologies, setTechnologies] = useState(project.technologies);
  const [priority, setPriority] = useState(project.priority === null ? "" : String(project.priority));
  const [weather, setWeather] = useState<string>(project.weather ?? "");
  const patch = usePatchProject(project.slug);

  const changes: ProjectPatchInput = {};
  if (isDar && title.trim() && title.trim() !== project.title) changes.title = title.trim();
  if (description !== project.description) changes.description = description;
  if (Number(trl) !== project.trl) changes.trl = Number(trl);
  if (!sameList(technologies, project.technologies)) changes.technologies = technologies;
  if (isDar && priority !== (project.priority === null ? "" : String(project.priority)))
    changes.priority = priority === "" ? null : Number(priority);
  if (weather !== (project.weather ?? "")) changes.weather = (weather || null) as Weather | null;
  const dirty = Object.keys(changes).length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!dirty) return;
    patch.mutate(changes, { onSuccess: () => notifySuccess(t("Project saved")) });
  }

  return (
    <Panel>
      <PanelHeader ruled>
        <PanelTitle>{t("Overview")}</PanelTitle>
      </PanelHeader>
      <form onSubmit={submit} noValidate>
        <PanelContent>
          <div className="ada-form">
            {isDar ? (
              <Field>
                <FieldLabel htmlFor="edit-title">{t("Title")}</FieldLabel>
                <Input id="edit-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
                <FieldDescription>{t("Only the DAR renames a project; the project's address does not change.")}</FieldDescription>
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor="edit-description">{t("Description")}</FieldLabel>
              <Textarea id="edit-description" rows={8} value={description} onChange={(e) => setDescription(e.target.value)} />
              <FieldDescription>{t("Plain text: line breaks are kept.")}</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-trl">{t("Maturity (TRL)")}</FieldLabel>
              <SelectField id="edit-trl" label={t("Maturity (TRL)")} value={trl} onChange={setTrl} options={TRL_OPTIONS} />
            </Field>
            {isDar ? (
              <Field>
                <FieldLabel htmlFor="edit-priority">{t("Priority")}</FieldLabel>
                <SelectField
                  id="edit-priority"
                  label={t("Priority")}
                  value={priority}
                  onChange={setPriority}
                  options={[{ value: "", label: t("Unranked") }, ...[0, 1, 2, 3].map((n) => ({ value: String(n), label: `P${n}` }))]}
                />
                <FieldDescription>{t("Sets the committee rhythm: P0 and P1 every 4 weeks, P2 every 6, P3 every 8.")}</FieldDescription>
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor="edit-weather">{t("Weather")}</FieldLabel>
              <SelectField
                id="edit-weather"
                label={t("Weather")}
                value={weather}
                onChange={setWeather}
                options={[{ value: "", label: t("Not given") }, ...(["green", "orange", "red"] as Weather[]).map((w) => ({ value: w, label: weatherLabel(w) }))]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-tech">{t("Technologies")}</FieldLabel>
              <TechnologiesField id="edit-tech" value={technologies} onChange={setTechnologies} />
            </Field>
          </div>
        </PanelContent>
        <PanelFooter>
          <Button type="submit" variant="primary" loading={patch.isPending} disabled={!dirty}>
            {t("Save the overview")}
          </Button>
        </PanelFooter>
      </form>
    </Panel>
  );
}

function StreamRow({ slug, stream, canArchive }: { slug: string; stream: Stream; canArchive: boolean }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(stream.title);
  const [description, setDescription] = useState(stream.description);
  const [confirm, setConfirm] = useState(false);
  const patch = usePatchStream(slug);
  const archive = useArchiveStream(slug);

  useEffect(() => {
    setTitle(stream.title);
    setDescription(stream.description);
  }, [stream.title, stream.description]);

  const dirty = title.trim() !== stream.title || description !== stream.description;
  const live = stream.members.length;

  return (
    <li className="ada-stack ada-stack--tight">
      <Field>
        <FieldLabel htmlFor={`stream-title-${stream.id}`}>
          {t("Title")}
          <span className="sr-only"> {t("of stream “{{name}}”", { name: stream.title })}</span>
        </FieldLabel>
        <Input id={`stream-title-${stream.id}`} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field>
        <FieldLabel htmlFor={`stream-desc-${stream.id}`}>
          {t("Description")}
          <span className="sr-only"> {t("of stream “{{name}}”", { name: stream.title })}</span>
        </FieldLabel>
        <Textarea id={`stream-desc-${stream.id}`} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="ada-cluster">
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || !title.trim()}
          loading={patch.isPending}
          onClick={() =>
            patch.mutate(
              { id: stream.id, title: title.trim(), description },
              { onSuccess: () => notifySuccess(t("Stream renamed"), title.trim()) },
            )
          }
        >
          {t("Save")}
        </Button>
        <Button size="sm" variant="ghost" disabled={!canArchive || live > 0} onClick={() => setConfirm(true)}>
          {t("Archive")}
        </Button>
        {live ? (
          <span className="ada-faint">
            {t("{{number}} current or upcoming assignment(s): the DAR must end them first.", { number: live })}
          </span>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t("Archive stream “{{name}}”?", { name: stream.title })}
        description={t("The stream disappears from the choices (requests, assignments). Its reports remain available.")}
        confirmLabel={t("Archive the stream")}
        pending={archive.isPending}
        onConfirm={() =>
          archive.mutate(stream.id, {
            onSuccess: () => {
              setConfirm(false);
              notifySuccess(t("Stream archived"), stream.title);
            },
          })
        }
      />
    </li>
  );
}

function StreamsSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const active = project.streams.filter((s) => !s.archived);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const add = useAddStream(project.slug);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    add.mutate(
      { title: title.trim(), description },
      {
        onSuccess: () => {
          notifySuccess(t("Stream added"), title.trim());
          setTitle("");
          setDescription("");
        },
      },
    );
  }

  return (
    <Panel>
      <PanelHeader ruled>
        <PanelTitle>{t("Streams")}</PanelTitle>
      </PanelHeader>
      <PanelContent>
        <div className="ada-stack">
          <ul className="ada-list">
            {active.map((stream) => (
              <StreamRow key={stream.id} slug={project.slug} stream={stream} canArchive={active.length > 1} />
            ))}
          </ul>
          {active.length <= 1 ? <p className="ada-faint">{t("A project keeps at least one active stream.")}</p> : null}
          <form className="ada-form" onSubmit={submit} noValidate aria-label={t("Add a stream")}>
            <Field>
              <FieldLabel htmlFor="new-stream-title">{t("New stream")}</FieldLabel>
              <Input id="new-stream-title" value={title} maxLength={200} placeholder={t("Title")} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="new-stream-desc">{t("Description (optional)")}</FieldLabel>
              <Textarea id="new-stream-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <div>
              <Button type="submit" variant="outline" loading={add.isPending} disabled={!title.trim()}>
                {t("Add the stream")}
              </Button>
            </div>
          </form>
        </div>
      </PanelContent>
    </Panel>
  );
}

interface LinkDraft {
  key: number;
  url: string;
  label: string;
  streamId: string;
}

const toDrafts = (links: ProjectLink[]): LinkDraft[] =>
  links.map((l, i) => ({ key: i, url: l.url, label: l.label, streamId: l.stream_id ? String(l.stream_id) : "" }));

function LinksSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LinkDraft[]>(() => toDrafts(project.links));
  const [nextKey, setNextKey] = useState(project.links.length);
  const save = useSetLinks(project.slug);
  const errors = rows.map((r) => linkUrlError(r.url));
  const streamOptions = [
    { value: "", label: t("Whole project") },
    ...project.streams.filter((s) => !s.archived).map((s) => ({ value: String(s.id), label: s.title })),
  ];

  function update(key: number, patch: Partial<LinkDraft>) {
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (errors.some(Boolean)) return;
    save.mutate(
      rows
        .filter((r) => r.url.trim())
        .map((r) => ({ url: r.url.trim(), label: r.label.trim(), stream_id: r.streamId ? Number(r.streamId) : null })),
      {
        onSuccess: (detail) => {
          notifySuccess(t("Links saved"));
          setRows(toDrafts(detail.links));
          setNextKey(detail.links.length);
        },
      },
    );
  }

  return (
    <Panel>
      <PanelHeader ruled>
        <PanelTitle>{t("Links")}</PanelTitle>
      </PanelHeader>
      <form onSubmit={submit} noValidate>
        <PanelContent>
          <div className="ada-stack">
            <p className="ada-faint">
              {t(
                "GitHub or GitLab repositories, SharePoint folders, documentation, production… Code repositories also let Claude draft the report from the commits.",
              )}
            </p>
            {rows.length ? (
              <ul className="ada-list">
                {rows.map((row, index) => (
                  <li key={row.key} className="ada-stack ada-stack--tight">
                    <Field>
                      <FieldLabel htmlFor={`link-url-${row.key}`}>
                        {t("Address")}
                        <span className="sr-only"> {index + 1}</span>
                      </FieldLabel>
                      <Input
                        id={`link-url-${row.key}`}
                        value={row.url}
                        placeholder="https://github.com/DiametralGroup/…"
                        aria-invalid={errors[index] ? true : undefined}
                        onChange={(e) => update(row.key, { url: e.target.value })}
                      />
                      <FieldError>{errors[index]}</FieldError>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor={`link-label-${row.key}`}>
                        {t("Label (optional)")}
                        <span className="sr-only"> {index + 1}</span>
                      </FieldLabel>
                      <Input
                        id={`link-label-${row.key}`}
                        value={row.label}
                        maxLength={200}
                        placeholder={t("e.g. Main repository, Kick-off deck, Production")}
                        onChange={(e) => update(row.key, { label: e.target.value })}
                      />
                    </Field>
                    <div className="ada-cluster">
                      <SelectField
                        label={t("Stream of link {{number}}", { number: index + 1 })}
                        value={row.streamId}
                        onChange={(v) => update(row.key, { streamId: v })}
                        options={streamOptions}
                        size="sm"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                      >
                        {t("Remove")}
                        <span className="sr-only"> {t("the link {{url}}", { url: row.url })}</span>
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ada-faint">{t("No link.")}</p>
            )}
            <div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setRows((current) => [...current, { key: nextKey, url: "", label: "", streamId: "" }]);
                  setNextKey((k) => k + 1);
                }}
              >
                {t("Add a link")}
              </Button>
            </div>
          </div>
        </PanelContent>
        <PanelFooter>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={errors.some(Boolean)}>
            {t("Save the links")}
          </Button>
        </PanelFooter>
      </form>
    </Panel>
  );
}

/** TL/PO (and DAR): description, TRL, technologies, streams and links. */
export function EditProjectSheet({
  open,
  onOpenChange,
  project,
  isDar,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
  isDar: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onOpenChange={(next: boolean) => onOpenChange(next)}>
      <SheetContent side="right" className="ada-sheet-wide">
        <SheetHeader>
          <SheetTitle>{t("Edit the project")}</SheetTitle>
          <SheetDescription>{project.title}</SheetDescription>
        </SheetHeader>
        {open ? (
          <div className="ada-sheet-body">
            <InfoSection project={project} isDar={isDar} />
            <StreamsSection project={project} />
            <LinksSection project={project} />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
